// src/pages/InvoiceList.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Sidebar from "../components/Sidebar";
import Nav from "../components/Nav";
import InvoiceModal from "./ProjectDetail/Invoice/InvoiceModal";
import InvoicePdfTemplate from "../components/InvoicePdfTemplate";
import generatePdfBlob from "../components/InvoicePdfExporter";
import "../styles/InvoiceList.css";

import Axios from "../utils/Axios";
import Toast from "../utils/Toast";
import AccessRestricted from "../components/AccessRestricted";
import { toISODateOnly } from "../utils/dateMaker";
import { CLIENTS_DETAILS_LIST, FETCH_PROJECTS, INVOICE_DETAIL } from "../config/API_Collection";
import { INVOICE_DETAIL_REQUEST } from "../store/Actions/Actions";

export default function InvoiceList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loginState = useSelector((s) => s?.login);
  const actions = loginState?.actions || {};
  const isBootstrapping = !!loginState?.isBootstrapping;

  const canListInvoices = actions?.invoices?.invoice_list === true;

  const [toast, setToast] = useState({ message: "", type: "" });
  const showToast = (message, type) => setToast({ message, type });

  // Filters
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);

  // invoices state
  const [invoices, setInvoices] = useState([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [invoiceError, setInvoiceError] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(100); // Default 100 invoices per page
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedInvoiceProp, setSelectedInvoiceProp] = useState(null);
  const [statusFilter, setStatusFilter] = useState('All');
  const [paymentFilter, setPaymentFilter] = useState('All');
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);
  const [pdfInvoiceData, setPdfInvoiceData] = useState(null);
  const [lastInvoiceSynced, setLastInvoiceSynced] = useState(null);
  
  // Get selected invoice from Redux
  const { selectedInvoice } = useSelector((state) => state.invoices || {});
  
  const handleDownloadInvoice = async (inv) => {
    const invoiceId = inv.id || inv.invoice_id;
    setDownloadingInvoiceId(invoiceId);
    console.log('Download initiated for invoice ID:', inv);
    
    try {
      // Dispatch Redux action to load invoice details
      dispatch({ type: INVOICE_DETAIL_REQUEST, payload: { id: invoiceId } });
      
      // Wait for Redux to update with the correct invoice data
      setTimeout(() => {
        // Use selectedInvoice from Redux if it matches the requested invoice, otherwise use inv
        const invoiceData = (selectedInvoice && (selectedInvoice.id === invoiceId || selectedInvoice.invoice_id === invoiceId))
          ? selectedInvoice 
          : inv;

         
        
        // Set data for hidden PDF template
        const subTotal = Number(invoiceData.amount || invoiceData.sub_total || 0);
        const gstRate = Number(invoiceData.gst || 0.15);
        const gstAmount = subTotal * gstRate;
        const total = subTotal + gstAmount;
        
        setPdfInvoiceData({
          invoiceNo: invoiceData.xero_no || inv.invoice_no || 'INV-XXXX',
          invoiceDate: invoiceData.invoice_date || invoiceData.date || inv.invoice_date || '-',
          subtotal: subTotal,
          gstAmount: gstAmount,
          total: total,
          dueDate: invoiceData.due_date || invoiceData.dueDate || inv.due_date || '-',
          clientName: invoiceData.client_name || inv.client_name || '',
          contactName: invoiceData.billing_contact || inv.billing_contact || '',
          clientAddress: invoiceData.client_address || inv.client_address || '',
          invoiceTitle: invoiceData.invoice_title || inv.invoice_title || '',
          description: invoiceData.description || inv.description || '',
          reference: invoiceData.reference || inv.reference || ''
        });
        
        // Wait a bit more for template to render
        setTimeout(async () => {
          try {
            const blob = await generatePdfBlob('invoice-download-template');
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const fileName = invoiceData.invoice_title 
              ? `Invoice ${invoiceData.xero_no || inv.invoice_no} - ${invoiceData.invoice_title}.pdf`
              : `Invoice ${invoiceData.xero_no || inv.invoice_no}.pdf`;
            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            setPdfInvoiceData(null);
          } catch (err) {
            console.error('PDF generation failed', err);
            showToast('Failed to generate PDF. Please try again.', 'danger');
            setPdfInvoiceData(null);
          } finally {
            setDownloadingInvoiceId(null);
          }
        }, 300);
      }, 500);
    } catch (err) {
      console.error('Failed to load invoice details', err);
      showToast('Failed to download invoice. Please try again.', 'danger');
      setDownloadingInvoiceId(null);
    }
  };
  

  useEffect(() => {
    if (!canListInvoices) return;
    // fetch clients and projects for filters (best-effort; ignore failures)
    (async () => {
      try {
        setIsLoadingClients(true);
        const res = await Axios.get(CLIENTS_DETAILS_LIST);
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : res?.data || [];
        setClients(list);
      } catch (e) {
        /* ignore */
      } finally {
        setIsLoadingClients(false);
      }
    })();

    (async () => {
      try {
        setIsLoadingProjects(true);
        const res = await Axios.get(FETCH_PROJECTS);
        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : res?.data || [];
        setProjects(list);
      } catch (e) {
        /* ignore */
      } finally {
        setIsLoadingProjects(false);
      }
    })();
  }, [canListInvoices]);

  // Fetch invoice list (single endpoint as requested)
  useEffect(() => {
    if (!canListInvoices) return;
    let isMounted = true;
    const fetchInvoices = async () => {
      try {
        setIsLoadingInvoices(true);
        setInvoiceError(null);
        const resp = await Axios.get("/invoices/invoice_list");
        const data = resp?.data?.data || resp?.data || [];
        setLastInvoiceSynced(data?.last_xero_sync_date || null);
        if (!isMounted) return;
        // If the response wraps with client/project and invoices, try to normalize
        if (Array.isArray(data)) {
          setInvoices(data);
        } else if (Array.isArray(resp?.data?.invoices)) {
          setInvoices(resp.data.invoices);
        } else if (Array.isArray(resp?.data)) {
          setInvoices(resp.data);
        } else {
          // fallback: if object contains `invoices` or `data.invoices`
          const arr = data.invoices || data.data || [];
          setInvoices(Array.isArray(arr) ? arr : []);
        }
      } catch (err) {
        console.error("Error fetching invoices:", err);
        setInvoiceError(err?.message || "Could not load invoices");
      } finally {
        if (isMounted) setIsLoadingInvoices(false);
      }
    };
    fetchInvoices();
    return () => {
      isMounted = false;
    };
  }, [canListInvoices]);

  const clientOptions = useMemo(() => {
    return (clients || []).map((c) => ({
      label: c?.client_name || c?.name || "",
      value: c?.client_id ?? c?.id,
    }));
  }, [clients]);

  if (isBootstrapping) return null; // app is bootstrapping
  if (!canListInvoices) return <AccessRestricted homePath="projects" />;

  // Filter + pagination
  const STATUS_LABELS = {
    0: 'Draft',
    1: 'Submitted',
    2: 'Approved',
  };

  const filtered = invoices.filter((inv) => {
    // apply status & payment filters first
    const rawStatus = inv.status ?? inv.invoice_status ?? 0;
    const statusLabel = STATUS_LABELS[Number(rawStatus)] ?? String(rawStatus || '');
    const rawPay = inv.xero_status ?? inv.payment_status ?? inv.paymentStatus ?? 0;
    const payLabel = Number(rawPay) === 1 ? 'Paid' : 'Pending';

    if (statusFilter !== 'All' && statusLabel !== statusFilter) return false;
    if (paymentFilter !== 'All' && payLabel !== paymentFilter) return false;

    // search across a few key fields + status/payment labels
    if (!search) return true;
    const s = String(search).toLowerCase();
    if (String(inv.invoice_id || inv.id || inv.invoiceId || '').toLowerCase().includes(s)) return true;
    if (String(inv.invoice_no || inv.invoice_no || inv.invoice_title || '').toLowerCase().includes(s)) return true;
    if (String(inv.project_name || inv.project || '').toLowerCase().includes(s)) return true;
    if (String(inv.client_name || inv.client || '').toLowerCase().includes(s)) return true;
    if (String(statusLabel).toLowerCase().includes(s)) return true;
    if (String(payLabel).toLowerCase().includes(s)) return true;
    return false;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const pageItems = filtered.slice(
    (pageSafe - 1) * pageSize,
    pageSafe * pageSize
  );

  // Build a condensed page list: show `1 2 3 ... last` when there are many pages
  const buildPageButtons = (current, total) => {
    if (total <= 3) return Array.from({ length: total }, (_, i) => i + 1);

    // Keep original behaviour for first two pages
    if (current <= 2) return [1, 2, '...', total];

    // If we're near the end, show the last three pages
    if (current >= total - 1) {
      const start = Math.max(1, total - 2);
      return Array.from({ length: total - start + 1 }, (_, i) => start + i);
    }

    // Default: show current as first, next page, ellipsis, last
    return [current, current + 1, '...', total];
  };
  const pageButtons = buildPageButtons(pageSafe, totalPages);

  const handleViewInvoice = (inv) => {
    const id = inv.id || inv.invoice_id || inv.invoiceId;
    if (!id) {
      // if no id, just set modal open with invoice prop
      setSelectedInvoiceProp(inv);
      setModalOpen(true);
      return;
    }
    setSelectedInvoiceProp(inv);
    dispatch({ type: INVOICE_DETAIL_REQUEST, payload: { id } });
    setModalOpen(true);
  };

  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) setPage(p);
  };

  return (
    <div style={{ display: "flex", height: "100vh" }}>
      <Sidebar />
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        <Nav />
        {isLoadingInvoices && <div className="top-loading-bar" />}
        <Toast message={toast.message} type={toast.type} />

        <div className="project-list-container">
          <div className="row align-items-center mb-3 justify-content-start">
            <div className="col-12 col-sm-6 col-lg-4">
              <input
                className="form-control"
                placeholder="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            <div className="col-6 col-sm-3 col-lg-2">
              <select
                className="form-select"
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              >
                <option value="All">All Invoice Status</option>
                <option value="Draft">Draft</option>
                <option value="Submitted">Submitted</option>
                <option value="Approved">Approved</option>
              </select>
            </div>

            <div className="col-6 col-sm-3 col-lg-2">
              <select
                className="form-select"
                value={paymentFilter}
                onChange={(e) => { setPaymentFilter(e.target.value); setPage(1); }}
              >
                <option value="All">All Payment Status</option>
                <option value="Pending">Pending</option>
                <option value="Paid">Paid</option>
              </select>
            </div>
            <div className="col-12 col-sm-12 col-lg-2 mt-2 mt-lg-0">
              <span className="me-3 font-weight-500 small">
  Last Synced: {lastInvoiceSynced ? lastInvoiceSynced : "Never"}
</span>

            </div>

           
          </div>

          <div className="card-body">
            {invoiceError && (
              <div className="alert alert-danger">{invoiceError}</div>
            )}
            <div className="table-responsive">
              <table className="table custom-table">
                <thead>
                  <tr>
                    <th>Invoice ID</th>
                    <th>Project</th>
                    <th>Client</th>
                    <th>Invoice Date</th>
                    <th className="text-end">Amount</th>
                    <th>Invoice Status</th>
                    <th>Paid Date</th>
                    <th>Payment Status</th>
                    
                    <th className="text-center" style={{ width: '50px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {isLoadingInvoices ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        Loading invoices...
                      </td>
                    </tr>
                  ) : pageItems.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="text-center py-4">
                        No invoices found
                      </td>
                    </tr>
                  ) : (
                    pageItems.map((inv) => {
                      const rawStatus = (inv.status ?? inv.invoice_status ?? 0);
                      const invStatus = Number(rawStatus);
                      const statusLabel = STATUS_LABELS[invStatus] ?? String(rawStatus ?? '');
                      const isApproved = invStatus === 2;
                      return (
                      <tr
                        key={inv.id || inv.invoice_id || inv.invoiceId}
                        onClick={() => handleViewInvoice(inv)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{!inv.invoice_no || inv.invoice_no === "null" ? "Pending" : inv.invoice_no}</td>
                        <td>{inv.project_name || inv.project || ""}</td>
                        <td>{inv.client_name || inv.client || ""}</td>
                        <td>{inv.invoice_date }</td>
                        <td className="text-end">${(Number(inv.total || 0)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td>{statusLabel}</td>
                        <td>{inv.paid_date || "N/A"}</td>
                        <td><span className={`status-pill status-${(inv.xero_status === 1 && inv.paid_full === 1) ? 'paid' : (inv.xero_status === 1 && inv.paid_full === 0) ? 'partially-paid' : 'pending'}`}>
                          {(inv.xero_status === 1 && inv.paid_full === 1) ? 'Paid' : (inv.xero_status === 1 && inv.paid_full === 0) ? 'Partially Paid' : 'Pending'}</span>  </td>
                        
                        <td className="text-center" onClick={(e) => e.stopPropagation()}>
                          {isApproved && (
                            <button
                              className="btn btn-link p-0 text-primary"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownloadInvoice(inv);
                              }}
                              title="Download Invoice"
                              style={{ fontSize: '18px', lineHeight: 1 }}
                              disabled={downloadingInvoiceId === (inv.id || inv.invoice_id)}
                            >
                              {downloadingInvoiceId === (inv.id || inv.invoice_id) ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                <i className="bi bi-download"></i>
                              )}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Rows per page selector - bottom right */}
            <div className="d-flex justify-content-end align-items-center mt-3 gap-2">
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Show</span>
              <select
                className="form-select"
                style={{ width: '100px', fontSize: '13px' }}
                value={pageSize === filtered.length && filtered.length > 500 ? "all" : pageSize}
                onChange={(e) => {
                  if (e.target.value === "all") {
                    setPageSize(filtered.length);
                  } else {
                    setPageSize(Number(e.target.value));
                  }
                  setPage(1); // Reset to first page
                }}
              >
                <option value="100">100</option>
                <option value="200">200</option>
                <option value="500">500</option>
                <option value="all">All</option>
              </select>
            </div>
          </div>
        </div>

        {/* Invoice view modal */}
        <InvoiceModal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            setSelectedInvoiceProp(null);
          }}
          invoice={selectedInvoiceProp}
          projectName={
            selectedInvoiceProp?.project_name ||
            selectedInvoiceProp?.project ||
            ""
          }
          hideBudgets={false}
        />

        {/* Hidden PDF template for download */}
        {pdfInvoiceData && (
          <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
            <InvoicePdfTemplate 
              id="invoice-download-template"
              invoiceNo={pdfInvoiceData.invoiceNo}
              invoiceDate={pdfInvoiceData.invoiceDate}
              subtotal={pdfInvoiceData.subtotal}
              gstAmount={pdfInvoiceData.gstAmount}
              total={pdfInvoiceData.total}
              dueDate={pdfInvoiceData.dueDate}
              clientName={pdfInvoiceData.clientName}
              contactName={pdfInvoiceData.contactName}
              clientAddress={pdfInvoiceData.clientAddress}
              invoiceTitle={pdfInvoiceData.invoiceTitle}
              description={pdfInvoiceData.description}
              reference={pdfInvoiceData.reference}
            />
          </div>
        )}

      </div>
    </div>
  );
}
